/**
 * Karte — die Grundfläche für alles Inhaltliche.
 *
 * Übernommen aus `components/data/Card.jsx`. Weiß, eine Haarlinie, ein weicher
 * Schatten; keine Verläufe, keine Texturen.
 *
 * `accent` färbt den oberen Streifen und ist der **einzige** Ort, an dem eine
 * Spartenfarbe erscheinen darf: als Wegweisung, nicht als Datenaussage
 * (ADR 0013). Eine Größenfarbe gehört hier nie hin.
 */
import type { CSSProperties, ReactNode } from "react";
import { fontWeight } from "../tokens.js";

export interface CardProps {
  readonly children: ReactNode;
  readonly title?: ReactNode;
  readonly subtitle?: ReactNode;
  /** Rechts neben dem Titel — üblicherweise ein Knopf. */
  readonly action?: ReactNode;
  readonly padding?: "none" | "sm" | "md" | "lg";
  /** Farbe des Akzentstreifens. Nur Spartenfarben, nie Serienfarben. */
  readonly accent?: string;
  readonly style?: CSSProperties;
}

const PADDINGS = { none: 0, sm: 12, md: 16, lg: 20 } as const;

export function Card({
  children,
  title,
  subtitle,
  action,
  padding = "md",
  accent,
  style,
}: CardProps) {
  const pad = PADDINGS[padding];

  return (
    <section
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
        borderTop: accent ? `3px solid ${accent}` : "1px solid var(--border-subtle)",
        overflow: "hidden",
        ...style,
      }}
    >
      {(title !== undefined || action !== undefined) && (
        <header
          style={{
            display: "flex",
            /* **`flex-wrap` seit HM-078.** Eine Aktion aus zwei Umschaltern
               passt auf ein Telefon nicht neben den Titel; ohne Umbruch schob
               sie sich unter den rechten Rand, und `overflow: hidden` der
               Karte schnitt sie ab — der Umschalter „Energie / Leistung" war
               auf dem Handy schlicht nicht da. Jetzt rückt sie in die nächste
               Zeile, sobald es eng wird. */
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
            padding: `${pad}px ${pad}px 0`,
          }}
        >
          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
            {title !== undefined && (
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 15,
                  fontWeight: fontWeight.semibold,
                  color: "var(--text-strong)",
                  letterSpacing: "var(--tracking-snug)",
                }}
              >
                {title}
              </h2>
            )}
            {subtitle !== undefined && (
              <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "2px 0 0" }}>
                {subtitle}
              </p>
            )}
          </div>
          {action !== undefined && (
            <div style={{ flex: "0 1 auto", minWidth: 0, maxWidth: "100%" }}>{action}</div>
          )}
        </header>
      )}
      <div style={{ padding: pad }}>{children}</div>
    </section>
  );
}
